import React from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, CheckCheck, ChevronRight, AlertTriangle, Siren } from "lucide-react";

export const NOTIF_TYPE_STYLE = {
  followup:        { bg: "bg-amber-500",   icon: <Bell size={12} className="text-white" />,          urgent: false },
  site_generated:  { bg: "bg-purple-600",  icon: <Bell size={12} className="text-white" />,          urgent: false },
  site:            { bg: "bg-emerald-600", icon: <Bell size={12} className="text-white" />,          urgent: false },
  email_sent:      { bg: "bg-blue-500",    icon: <Bell size={12} className="text-white" />,          urgent: false },
  payment:         { bg: "bg-green-500",   icon: <Bell size={12} className="text-white" />,          urgent: false },
  system:          { bg: "bg-gray-600",    icon: <Bell size={12} className="text-white" />,          urgent: false },
  inactivity_15d:  { bg: "bg-amber-500",   icon: <AlertTriangle size={12} className="text-white" />, urgent: false },
  churn_alert_30d: { bg: "bg-red-600",     icon: <Siren size={12} className="text-white" />,         urgent: true  },
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const m = Math.floor((Date.now() - d) / 60000);
  if (m < 1) return "Ahora";
  if (m < 60) return `Hace ${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Hoy a las ${d.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" })}`;
  if (h < 48) return `Ayer a las ${d.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" })}`;
  return d.toLocaleDateString("es-UY", { day: "numeric", month: "short" });
}

export function NotificationsPanel({
  panelRef, notifList, notifTab, archivingAll,
  onTabChange, onArchiveOne, onArchiveAll, onClose,
}) {
  const navigate = useNavigate();
  return (
    <div
      ref={panelRef}
      data-testid="notif-panel"
      className="fixed z-50 bg-[#111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden
        md:left-[96px] md:bottom-4 md:w-80
        left-4 right-4 bottom-[72px]"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <span className="text-sm font-bold text-white">Notificaciones</span>
        <div className="flex items-center gap-2">
          <button
            data-testid="archive-all-btn"
            onClick={onArchiveAll}
            disabled={archivingAll}
            className="text-[10px] font-semibold text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
          >
            <CheckCheck size={11} /> Archivar todo
          </button>
          <button onClick={onClose} className="text-gray-500 hover:text-white ml-1">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        {[["nueva", "Nueva"], ["archivada", "Archivada"], ["todos", "Todos"]].map(([val, lbl]) => (
          <button
            key={val}
            data-testid={`notif-tab-${val}`}
            onClick={() => onTabChange(val)}
            className={`flex-1 py-2 text-xs font-semibold transition-colors ${
              notifTab === val
                ? "text-[#ffc700] border-b-2 border-[#ffc700]"
                : "text-gray-500 hover:text-gray-300"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="max-h-[55vh] overflow-y-auto">
        {notifList.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell size={24} className="text-white/10 mx-auto mb-2" />
            <p className="text-xs text-gray-500">Sin notificaciones</p>
          </div>
        ) : (
          notifList.map(n => {
            const style = NOTIF_TYPE_STYLE[n.type] || NOTIF_TYPE_STYLE.system;
            const isUrgent = style.urgent;
            return (
              <div
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                className={`flex gap-3 px-4 py-3 border-b last:border-0 group transition-colors
                  ${isUrgent
                    ? "bg-red-950/40 border-red-500/20 hover:bg-red-950/60"
                    : "hover:bg-white/5 border-white/5"
                  }`}
              >
                <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 ${style.bg} ${isUrgent ? "ring-2 ring-red-500/40 animate-pulse" : ""}`}>
                  {style.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-xs font-black leading-snug ${isUrgent ? "text-red-300" : "text-white"}`}>{n.title}</p>
                    <button
                      onClick={() => onArchiveOne(n.id)}
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-gray-600 hover:text-gray-300 transition-all"
                    >
                      <X size={11} />
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(n.created_at)}</p>
                  {n.description && (
                    <p className={`text-[11px] mt-1 leading-snug ${isUrgent ? "text-red-200/80" : "text-gray-300"}`}>{n.description}</p>
                  )}
                  {/* Botón para ir a cuentas en alertas de inactividad */}
                  {(n.type === "churn_alert_30d" || n.type === "inactivity_15d") && (
                    <button
                      onClick={() => { navigate("/accounts"); onClose(); }}
                      className={`mt-1.5 flex items-center gap-1 text-[10px] font-semibold hover:underline ${isUrgent ? "text-red-400" : "text-amber-400"}`}
                    >
                      Ver en Cuentas <ChevronRight size={10} />
                    </button>
                  )}
                  {n.contact_id && n.type !== "churn_alert_30d" && n.type !== "inactivity_15d" && (
                    <button
                      onClick={() => { navigate(`/crm/${n.contact_id}`); onClose(); }}
                      className="mt-1.5 flex items-center gap-1 text-[10px] text-[#ffc700] font-semibold hover:underline"
                    >
                      Ver contacto <ChevronRight size={10} />
                    </button>
                  )}
                  {n.business_id && n.type === "site" && (
                    <button
                      data-testid={`notif-view-biz-${n.id}`}
                      onClick={() => { navigate(`/business/${n.business_id}`); onClose(); }}
                      className="mt-1.5 flex items-center gap-1 text-[10px] text-emerald-400 font-semibold hover:underline"
                    >
                      Ver negocio <ChevronRight size={10} />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
